import { appendMarketingCampaign } from "../lib/marketingTracking";
import { WebsiteHandoffRecovery } from "../components/WebsiteHandoffRecovery";
import { internalReturnPath } from "../lib/onboardingIntent";
import { LOGIN_REASON_COPY, loginReason } from "../lib/loginReason";
import { websiteHandoffParams, resumeWebsiteHandoff, WebsiteHandoffError } from "../lib/websiteHandoff";
import { sendSignInLinkToEmail } from "firebase/auth";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { isValidPrivateCode } from "../lib/privateCodes";
import {
  PENDING_INVITE_TOKEN_KEY,
  returnToWithPendingInviteToken,
} from "../lib/workspaceInviteIntent";
import { auth } from "../services/Firebase"; // Adjust the path based on your project structure

// Detect if running in an in-app browser (WebView) where Google OAuth is blocked
const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || "";
  // Check for common in-app browsers
  const inAppPatterns = [
    /LinkedIn/i, // LinkedIn in-app browser
    /FBAN|FBAV|FB_IAB/i, // Facebook in-app browser
    /Instagram/i, // Instagram in-app browser
    /Twitter/i, // Twitter/X in-app browser
    /BytedanceWebview|TikTok/i, // TikTok in-app browser
    /; wv\)/i, // Android WebView marker
  ];
  return inAppPatterns.some((pattern) => pattern.test(ua));
};

const isAndroid = (): boolean => /Android/i.test(navigator.userAgent || "");

// Android in-app browsers hand intent:// links to the named app, so this opens
// Chrome directly; the fallback keeps the visitor here if Chrome is missing.
export const chromeIntentUrl = (href: string): string => {
  const url = new URL(href);
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
};

// Firebase messages read like "Firebase: Error (auth/invalid-email)."; say what to do instead.
export const emailLinkErrorMessage = (code: string | undefined): string => {
  switch (code) {
    case "auth/invalid-email":
    case "auth/missing-email":
      return "That email address doesn't look right. Please check it and try again.";
    case "auth/network-request-failed":
      return "We couldn't reach our sign-in service. Check your connection and try again.";
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return "Too many attempts. Please wait a minute and try again.";
    default:
      return `We couldn't send the sign-in link. Please try again${code ? ` (${code})` : ""}.`;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [planId, setPlanId] = useState<string | null>(null);
  const [privateCode, setPrivateCode] = useState<string | null>(null);
  const [articleUrlParam, setArticleUrlParam] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [handoffError, setHandoffError] = useState<WebsiteHandoffError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // The popup handler owns navigation once sign-in starts. Auth state can
  // update before its promise resolves; the automatic redirect must not
  // consume the handoff and let the handler overwrite it with /generate.
  const googleOwnsRedirect = useRef(false);
  const autoHandoff = useRef<{ key: string; task: Promise<string> } | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);
  // Phones often hide the area below the button behind the keyboard.
  const emailResultRef = useRef<HTMLDivElement>(null);
  const [inAppBrowser] = useState(() => isInAppBrowser());
  const [androidInApp] = useState(() => inAppBrowser && isAndroid());
  const [linkCopied, setLinkCopied] = useState(false);
  const { signInWithGoogle, createCustomerIfNeeded, currentUser, loading: authLoading } = useAuth();

  const getReturnToPath = (): string => {
    const explicitReturn = internalReturnPath(searchParams.get("returnTo"));
    if (explicitReturn) return explicitReturn;
    const state = location.state as
      | {
          from?: { pathname?: string; search?: string; hash?: string };
        }
      | undefined;
    const from = state?.from;

    if (!from?.pathname) {
      return "/generate";
    }

    return `${from.pathname}${from.search || ""}${from.hash || ""}`;
  };

  const reason = loginReason(searchParams, getReturnToPath());
  const reasonCopy = reason ? LOGIN_REASON_COPY[reason] : null;
  const reportedReason = useRef(false);
  // Signed-in visitors are redirected straight on; only count people asked to sign in.
  useEffect(() => {
    if (reportedReason.current || authLoading || currentUser) return;
    reportedReason.current = true;
    posthog.capture("login_viewed", { reason: reason ?? "none", in_app_browser: inAppBrowser });
  }, [reason, inAppBrowser, authLoading, currentUser]);

  const externalBrowserUrl = (): string => {
    // The external browser cannot read this in-app browser's attribution cookie.
    const link = new URL(window.location.href);
    if (process.env.REACT_APP_META_TRACKING_ENABLED === "true") {
      appendMarketingCampaign(link.searchParams);
    }
    return link.toString();
  };

  const handleCopyLink = async () => {
    const handoffUrl = externalBrowserUrl();
    try {
      await navigator.clipboard.writeText(handoffUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = handoffUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  useEffect(() => {
    const urlPlanId = searchParams.get("planId");
    if (urlPlanId) {
      setPlanId(urlPlanId);
    }

    const urlPrivateCode = searchParams.get("privateCode");
    if (urlPrivateCode) {
      if (isValidPrivateCode(urlPrivateCode)) {
        setPrivateCode(urlPrivateCode);
      } else {
        console.warn("Invalid promotion code provided:", urlPrivateCode);
        setError(
          "Invalid promotion code provided. Please check and try again."
        );
        setPrivateCode(null);
      }
    }

    const articleUrl = searchParams.get("articleUrl");
    if (articleUrl) {
      setArticleUrlParam(articleUrl);
    }

    // Store claim params for website-to-app carousel continuity
    const claimPost = searchParams.get("claimPost");
    const claimToken = searchParams.get("claimToken");
    const claimIntent = searchParams.get("intent");
    const pendingContent = searchParams.get("pendingContent");

    if (claimPost && claimToken) {
      window.sessionStorage.setItem("claimPost", claimPost);
      window.sessionStorage.setItem("claimToken", claimToken);
      if (claimIntent) {
        window.sessionStorage.setItem("claimIntent", claimIntent);
      }
    } else if (pendingContent && claimToken) {
      // Transient text content from website tools (e.g. text-to-generate)
      window.sessionStorage.setItem("pendingContent", pendingContent);
      window.sessionStorage.setItem("claimToken", claimToken);
      if (claimIntent) {
        window.sessionStorage.setItem("claimIntent", claimIntent);
      }
    }
  }, [searchParams]);

  // Auto-redirect already-authenticated users who land on /login with claim params
  // or a returnTo location (e.g. came from website deep link but were already signed in).
  useEffect(() => {
    if (authLoading || !currentUser || googleOwnsRedirect.current) return;

    let cancelled = false;
    const returnTo = getReturnToPath();
    const key = JSON.stringify([currentUser.uid, searchParams.toString(), returnTo]);
    // React can re-run the effect while a claim/clone is in flight. Reuse that
    // request so the shared-post path cannot create two copies.
    if (autoHandoff.current?.key !== key) {
      autoHandoff.current = { key, task: resumeWebsiteHandoff(currentUser, websiteHandoffParams(searchParams), returnTo) };
    }
    void autoHandoff.current.task
      .then(target => { if (!cancelled) navigate(target, { replace: true }); })
      .catch(err => { if (!cancelled) setHandoffError(err instanceof WebsiteHandoffError ? err : new WebsiteHandoffError("https://wavegen.ai/")); });
    return () => { cancelled = true; };
  }, [authLoading, currentUser, searchParams, navigate, location.state]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError(null);
    setEmailSent(null);

    try {
      // The invite fragment is scrubbed before React starts and retained in
      // sessionStorage. Put it back only in the email action's return path so
      // a new sign-in tab can restore the invitation before account bootstrap.
      const returnTo = returnToWithPendingInviteToken(
        getReturnToPath(),
        window.sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY),
      );

      // Store private code in sessionStorage first, just like we do for Google sign-in
      if (privateCode && isValidPrivateCode(privateCode)) {
        console.log(
          "[DEBUG] Login: Storing privateCode in sessionStorage:",
          privateCode
        );
        window.sessionStorage.setItem("pendingPrivateCode", privateCode);
      } else {
        window.sessionStorage.removeItem("pendingPrivateCode");
      }

      const queryParams = websiteHandoffParams(searchParams);
      if (process.env.REACT_APP_META_TRACKING_ENABLED === "true") appendMarketingCampaign(queryParams);
      if (planId) {
        queryParams.set("planId", planId);
      }
      if (privateCode && isValidPrivateCode(privateCode)) {
        queryParams.set("privateCode", privateCode);
      }
      if (articleUrlParam) {
        queryParams.set("articleUrl", articleUrlParam);
      }
      // Add email to URL so it works across devices/browsers
      queryParams.set("email", email);
      queryParams.set("returnTo", returnTo);
      const queryString = queryParams.toString();
      const finishUrl = `${window.location.origin}/finish-signin${queryString ? `?${queryString}` : ""}`;

      const actionCodeSettings = {
        url: finishUrl,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      window.localStorage.setItem("emailForSignIn", email);
      setEmailSent(email);
      posthog.capture("login_email_link_sent", { in_app_browser: inAppBrowser });
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      setEmailError(emailLinkErrorMessage(code));
      posthog.capture("login_email_link_failed", { error_code: code || "unknown", in_app_browser: inAppBrowser });
      // Clear storage on error
      window.sessionStorage.removeItem("pendingPrivateCode");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    googleOwnsRedirect.current = true;
    setGoogleLoading(true);
    setError(null);
    const from = getReturnToPath();

    try {
      if (planId) {
        window.sessionStorage.setItem("pendingPlanId", planId);
      } else {
        window.sessionStorage.removeItem("pendingPlanId");
      }
      if (privateCode && isValidPrivateCode(privateCode)) {
        window.sessionStorage.setItem("pendingPrivateCode", privateCode);
      } else {
        window.sessionStorage.removeItem("pendingPrivateCode");
      }
      if (articleUrlParam) {
        window.sessionStorage.setItem("pendingArticleUrl", articleUrlParam);
      } else {
        window.sessionStorage.removeItem("pendingArticleUrl");
      }

      await signInWithGoogle();
      if (auth.currentUser) await createCustomerIfNeeded(auth.currentUser);

      const target = await resumeWebsiteHandoff(auth.currentUser, websiteHandoffParams(searchParams), from);
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof WebsiteHandoffError) { setHandoffError(err); return; }
      setError(
        err instanceof Error ? err.message : "Failed to sign in with Google"
      );
      googleOwnsRedirect.current = false;
      // Only clear storage on error
      window.sessionStorage.removeItem("pendingPlanId");
      window.sessionStorage.removeItem("pendingPrivateCode");
      window.sessionStorage.removeItem("pendingArticleUrl");
      // Keep the website handoff so a cancelled popup can be retried.
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (emailSent || emailError) emailResultRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [emailSent, emailError]);

  if (handoffError) return <WebsiteHandoffRecovery error={handoffError} onRetry={async () => {
    if (!auth.currentUser) return;
    await createCustomerIfNeeded(auth.currentUser);
    const target = await resumeWebsiteHandoff(auth.currentUser, websiteHandoffParams(searchParams), getReturnToPath());
    navigate(target, { replace: true });
  }} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img
              src="/icon.svg" // Adjust the path to your logo
              alt="Company Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-center">
            {reasonCopy ? reasonCopy.title : "Welcome to WaveGen"}
          </CardTitle>
          <CardDescription className="text-center">
            {reasonCopy ? (
              reasonCopy.description
            ) : (
              <>
                We'll send you an email with a link to get started.<br></br>No
                prior registration is needed to login.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <Button type="submit" className="w-full" disabled={emailLoading}>
                {emailLoading
                  ? "Sending link..."
                  : reasonCopy
                    ? "Continue with email"
                    : "Send sign in link"}
              </Button>
            </div>
            <div ref={emailResultRef} aria-live="polite">
              {emailSent && (
                <Alert className="mt-4 border-green-300 bg-green-50 text-green-900 [&>svg]:text-green-700">
                  <MailCheck className="h-5 w-5" />
                  <AlertTitle>Check your inbox</AlertTitle>
                  <AlertDescription>
                    We've sent a sign in link to <strong>{emailSent}</strong>.
                    Not in your inbox? It may have landed in spam. Wrong
                    address? Fix it above and send again.
                  </AlertDescription>
                </Alert>
              )}
              {emailError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{emailError}</AlertDescription>
                </Alert>
              )}
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={inAppBrowser ? undefined : handleGoogleLogin}
              disabled={googleLoading || inAppBrowser}
              className="w-full mt-4"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {reasonCopy ? "Continue with Google" : "Sign in with Google"}
            </Button>

            {inAppBrowser && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                <p>
                  Google sign-in is blocked in this browser. Open in
                  browser to continue:
                </p>
                {androidInApp && (
                  <a
                    href={chromeIntentUrl(externalBrowserUrl())}
                    onClick={() => posthog.capture("login_open_in_chrome_clicked")}
                    className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-md bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-700"
                  >
                    Open in Chrome
                  </a>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="w-full mt-2 text-amber-800 border-amber-300 hover:bg-amber-100"
                >
                  {linkCopied ? "Link copied!" : "Copy link to open in browser"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter>
          {error && (
            <Alert variant="destructive" className="w-full">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
